import { Navigate } from "react-router";
import type { AppRouteGuard } from "../types";
import { authClient } from "../../auth/client";
import { createContext, useContext } from "react";
import { ObsidianUserId } from "@repo/lib-obsidian-ts/types";
import { SquareBackground } from "@repo/dashboard-components";

export const AuthenticatedRouteGuard: AppRouteGuard = (props) => {
  const session = authClient.useSession();

  if (session.isPending || session.isRefetching) {
    return <SquareBackground mode="loading" />;
  }

  if (!session.data) {
    return <Navigate to="/login" />;
  }

  return (
    <AuthenticatedRouteContext.Provider
      value={{
        session: {
          id: session.data.session.id,
          impersonatedBy: session.data.session.impersonatedBy ?? null,
        },
        user: {
          id: ObsidianUserId(session.data.user.id),
          role: session.data.user.role === "admin" ? "admin" : "user",
          name: session.data.user.name,
        },
      }}
    >
      {props.children}
    </AuthenticatedRouteContext.Provider>
  );
};

export type AuthenticatedSession = {
  id: string;
  impersonatedBy: string | null;
};

export type AuthenticatedUser = {
  id: typeof ObsidianUserId.Type;
  role: "admin" | "user";
  name: string;
};

export type AuthenticatedRouteContextData = {
  session: AuthenticatedSession;
  user: AuthenticatedUser;
};

const AuthenticatedRouteContext =
  createContext<AuthenticatedRouteContextData | null>(null);

export function useAuthenticated() {
  const data = useContext(AuthenticatedRouteContext);

  if (!data) {
    throw new Error(
      "useAuthenticated can only be called in an authenticated route",
    );
  }

  return data;
}
